import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAvatarUrl } from './gravatar';

interface AvatarDropdownProps {
  isLoggedIn: boolean;
  onLogout?: () => void;
}

export const AvatarDropdown: React.FC<AvatarDropdownProps> = ({ isLoggedIn, onLogout }) => {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [meEmail, setMeEmail] = useState<string | null>(null);
  const [avatarKey, setAvatarKey] = useState<string | null>(null);
  const [planName, setPlanName] = useState<string>('');

  const displayName = meEmail ? meEmail.split('@')[0] : 'Account';
  const planLabel = planName ? `${planName} Plan` : 'Member';
  const avatarSrc = getAvatarUrl(avatarKey);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      setMeEmail(null);
      setAvatarKey(null);
      setPlanName('');
      return;
    }

    (async () => {
      try {
        const r = await fetch('/api/me', { credentials: 'include' });
        if (!r.ok) throw new Error('Not logged in');
        const j = await r.json();
        setMeEmail(j?.user?.email || null);
        setAvatarKey(j?.user?.avatar_r2_key || null);
      } catch {
        setMeEmail(null);
      }
    })();

    (async () => {
      try {
        const r = await fetch('/api/storage/usage', { credentials: 'include' });
        const j = await r.json().catch(() => ({}));
        setPlanName(j?.plan?.name ? String(j.plan.name) : '');
      } catch {
        setPlanName('');
      }
    })();
  }, [isLoggedIn]);

  if (!isLoggedIn) {
    return (
      <>
        <button onClick={() => navigate('/auth/signin')} className="text-sm font-bold text-slate-600 hover:text-primary transition-colors">Sign In</button>
        <button onClick={() => navigate('/auth/signup')} className="bg-primary hover:bg-blue-600 text-white text-sm font-bold px-6 py-2.5 rounded-full transition-all active:scale-95">Get Started</button>
      </>
    );
  }

  return (
    <div className="relative" ref={menuRef}>
      {/* Desktop: Full layout with text and avatar */}
      <div className="hidden md:flex items-center gap-3 pr-2 cursor-pointer group" onClick={() => setShowUserMenu(!showUserMenu)}>
        <div className="text-right">
          <p className="text-[10px] font-black uppercase text-slate-400 leading-none mb-1">{planLabel}</p>
          <p className="text-xs font-bold text-slate-900 group-hover:text-primary transition-colors">{displayName}</p>
        </div>
        <div className="size-10 rounded-full border border-slate-200 overflow-hidden group-hover:ring-2 group-hover:ring-primary/20 transition-all">
          <img src={avatarSrc} alt="Avatar" className="w-full h-full object-cover" />
        </div>
      </div>

      {/* Mobile: Avatar only */}
      <div className="md:hidden size-8 rounded-full border border-slate-200 overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary/20 transition-all" onClick={() => setShowUserMenu(!showUserMenu)}>
        <img src={avatarSrc} alt="Avatar" className="w-full h-full object-cover" />
      </div>

      {showUserMenu && (
        <div className="absolute top-full right-0 mt-3 w-72 bg-white/95 backdrop-blur-xl rounded-[1.5rem] shadow-2xl border border-slate-100 overflow-hidden z-[110] animate-in fade-in zoom-in-95 duration-200">
          <div className="p-5 border-b border-slate-50 bg-slate-50/50">
            <div className="flex items-center gap-3 mb-3">
              <div className="size-10 rounded-full overflow-hidden border border-white shadow-sm">
                <img src={avatarSrc} alt="Avatar" className="w-full h-full object-cover" />
              </div>
              <div className="overflow-hidden">
                <p className="text-sm font-black text-slate-900 truncate">{displayName}</p>
                <p className="text-[10px] font-bold text-slate-400 truncate uppercase tracking-tight">{meEmail || ''}</p>
              </div>
            </div>
          </div>
          <div className="p-2">
            <button onClick={() => { setShowUserMenu(false); navigate('/profile'); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-50 hover:text-primary transition-all text-left">
              <span className="material-symbols-outlined text-xl">person</span> Profile
            </button>
            <button onClick={() => { setShowUserMenu(false); navigate('/billing'); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-50 hover:text-primary transition-all text-left">
              <span className="material-symbols-outlined text-xl">credit_card</span> Billing & Payments
            </button>
          </div>
          <div className="p-2 border-t border-slate-50">
            <button onClick={() => { setShowUserMenu(false); if (onLogout) onLogout(); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold text-red-500 hover:bg-red-50 transition-all text-left">
              <span className="material-symbols-outlined text-xl">logout</span> Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
